import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  Collection,
  CollectionCreatedEnvelope,
  CollectionDeletedEnvelope,
  CollectionQuery,
  CollectionUpdatedEnvelope,
  CollectionsQueryBuilder,
  DeleteCollectionResponse,
  GetCollectionOptions,
  ListCollectionsOptions,
  ListCollectionsResponse,
  QueryCollectionsOptions,
  QueryCollectionsResponse,
  UpdateCollection,
  createCollection as universalCreateCollection,
  deleteCollection as universalDeleteCollection,
  getCollection as universalGetCollection,
  listCollections as universalListCollections,
  queryCollections as universalQueryCollections,
  typedQueryCollections as universalTypedQueryCollections,
  updateCollection as universalUpdateCollection,
} from './portfolio-collections-v1-collection-collections.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/portfolio' };

export function createCollection(
  httpClient: HttpClient
): CreateCollectionSignature {
  return (collection: Collection) =>
    universalCreateCollection(
      collection,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateCollectionSignature {
  /**
   * Creates a collection.
   * @param - Collection to create.
   * @returns Created collection.
   */
  (collection: Collection): Promise<
    NonNullablePaths<
      Collection,
      | `coverImage.focalPoint.x`
      | `coverImage.focalPoint.y`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function getCollection(httpClient: HttpClient): GetCollectionSignature {
  return (collectionId: string, options?: GetCollectionOptions) =>
    universalGetCollection(
      collectionId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetCollectionSignature {
  /**
   * Retrieves a collection.
   * @param - ID of the collection to retrieve.
   * @returns The requested collection.
   */
  (collectionId: string, options?: GetCollectionOptions): Promise<
    NonNullablePaths<
      Collection,
      | `coverImage.focalPoint.x`
      | `coverImage.focalPoint.y`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function listCollections(
  httpClient: HttpClient
): ListCollectionsSignature {
  return (options?: ListCollectionsOptions) =>
    universalListCollections(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListCollectionsSignature {
  /**
   * Retrieves a list all collections in a portfolio.
   */
  (options?: ListCollectionsOptions): Promise<
    NonNullablePaths<
      ListCollectionsResponse,
      | `collections`
      | `collections.${number}.coverImage.focalPoint.x`
      | `collections.${number}.coverImage.focalPoint.y`
      | `collections.${number}.seoData.settings.preventAutoRedirect`,
      6
    >
  >;
}

export function updateCollection(
  httpClient: HttpClient
): UpdateCollectionSignature {
  return (
    _id: string,
    collection: NonNullablePaths<UpdateCollection, `revision`, 2>
  ) =>
    universalUpdateCollection(
      _id,
      collection,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateCollectionSignature {
  /**
   * Updates a collection.
   * @param - Collection ID.
   * @returns Updated collection.
   */
  (
    _id: string,
    collection: NonNullablePaths<UpdateCollection, `revision`, 2>
  ): Promise<
    NonNullablePaths<
      Collection,
      | `coverImage.focalPoint.x`
      | `coverImage.focalPoint.y`
      | `seoData.tags`
      | `seoData.tags.${number}.type`
      | `seoData.tags.${number}.children`
      | `seoData.tags.${number}.custom`
      | `seoData.tags.${number}.disabled`
      | `seoData.settings.preventAutoRedirect`
      | `seoData.settings.keywords`
      | `seoData.settings.keywords.${number}.term`
      | `seoData.settings.keywords.${number}.isMain`,
      6
    >
  >;
}

export function deleteCollection(
  httpClient: HttpClient
): DeleteCollectionSignature {
  return (collectionId: string) =>
    universalDeleteCollection(
      collectionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteCollectionSignature {
  /**
   * Deletes a collection.
   * @param - ID of the collection to delete.
   */
  (collectionId: string): Promise<
    NonNullablePaths<DeleteCollectionResponse, `collectionId`, 2>
  >;
}

export function queryCollections(
  httpClient: HttpClient
): QueryCollectionsSignature {
  return (options?: QueryCollectionsOptions) =>
    universalQueryCollections(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface QueryCollectionsSignature {
  /**
   * Creates a query to retrieve a list of collections.
   *
   * The `queryCollections()` method builds a query to retrieve a list of collections and returns a `CollectionsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is used to run the query using the [`find()`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-builder/find) method.
   *
   * You can refine the query by chaining `CollectionsQueryBuilder` methods onto the query. `CollectionsQueryBuilder` methods enable you to filter, sort, and control the results that `queryCollections()` returns.
   *
   * `queryCollections()`  runs with the following `CollectionsQueryBuilder` defaults which you can override:
   *
   * + [`limit(100)`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-builder/limit)
   * + [`ascending('_id')`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-builder/ascending)
   *
   * The following `CollectionsQueryBuilder` methods are supported for `queryCollections()`. For a full description of the operations object, see the object returned for the [`items`](https://dev.wix.com/docs/sdk/backend-modules/portfolio/collections/collections-query-result/items) property in `CollectionsQueryResult`.
   */
  (options?: QueryCollectionsOptions): CollectionsQueryBuilder;
}

export function typedQueryCollections(
  httpClient: HttpClient
): TypedQueryCollectionsSignature {
  return (query: CollectionQuery, options?: QueryCollectionsOptions) =>
    universalTypedQueryCollections(
      query,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryCollectionsSignature {
  /** */
  (query: CollectionQuery, options?: QueryCollectionsOptions): Promise<
    NonNullablePaths<
      QueryCollectionsResponse,
      | `collections`
      | `collections.${number}.coverImage.focalPoint.x`
      | `collections.${number}.coverImage.focalPoint.y`
      | `collections.${number}.seoData.settings.preventAutoRedirect`,
      6
    >
  >;
}

export const onCollectionCreated = EventDefinition(
  'wix.portfolio.collections.v1.collection_created',
  true,
  (event: CollectionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.sortOrder' },
            { path: 'entity.coverImage.focalPoint.x' },
            { path: 'entity.coverImage.focalPoint.y' },
            { path: 'entity.coverImage.unsharpMasking.amount' },
            { path: 'entity.coverImage.unsharpMasking.radius' },
            { path: 'entity.coverImage.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage.imageInfo' }],
        },
      ])
    )
)<CollectionCreatedEnvelope>();
export const onCollectionDeleted = EventDefinition(
  'wix.portfolio.collections.v1.collection_deleted',
  true,
  (event: CollectionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'undefined.sortOrder' },
            { path: 'undefined.coverImage.focalPoint.x' },
            { path: 'undefined.coverImage.focalPoint.y' },
            { path: 'undefined.coverImage.unsharpMasking.amount' },
            { path: 'undefined.coverImage.unsharpMasking.radius' },
            { path: 'undefined.coverImage.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'undefined.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.coverImage.imageInfo' }],
        },
      ])
    )
)<CollectionDeletedEnvelope>();
export const onCollectionUpdated = EventDefinition(
  'wix.portfolio.collections.v1.collection_updated',
  true,
  (event: CollectionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.sortOrder' },
            { path: 'entity.coverImage.focalPoint.x' },
            { path: 'entity.coverImage.focalPoint.y' },
            { path: 'entity.coverImage.unsharpMasking.amount' },
            { path: 'entity.coverImage.unsharpMasking.radius' },
            { path: 'entity.coverImage.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [{ path: 'entity.url' }],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.coverImage.imageInfo' }],
        },
      ])
    )
)<CollectionUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  AdminRemoveMenuItemsResponse,
  App,
  BaseEventMetadata,
  Collection,
  CollectionCreatedEnvelope,
  CollectionDeletedEnvelope,
  CollectionQuerySpec,
  CollectionUpdatedEnvelope,
  CollectionsQueryBuilder,
  CollectionsQueryResult,
  CreateCollectionRequest,
  CreateCollectionResponse,
  CursorPaging,
  Cursors,
  CustomTag,
  DeleteCollectionRequest,
  DeleteCollectionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  GetCollectionOptions,
  GetCollectionRequest,
  GetCollectionResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageType,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  Keyword,
  ListCollectionsOptions,
  ListCollectionsRequest,
  ListCollectionsResponse,
  MessageEnvelope,
  Page,
  Pages,
  Paging,
  PagingMetadataV2,
  Point,
  QueryCollectionsOptions,
  QueryCollectionsRequest,
  QueryCollectionsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  SeoSchema,
  Settings,
  SortOrder,
  Sorting,
  Tag,
  URI,
  URIs,
  UnsharpMasking,
  UpdateCollection,
  UpdateCollectionRequest,
  UpdateCollectionResponse,
  WebhookIdentityType,
  utils,
} from './portfolio-collections-v1-collection-collections.universal.js';
